/**
 * Spaces Service
 *
 * Handles all CRUD operations for shared spaces in Firestore.
 * Integrates with Google Drive to create/manage folder structure.
 */

import {
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth } from '../lib/firebase';
import { spacesCollection, membersCollection } from '../lib/firestore-collections';
import type {
  Space,
  CreateSpaceInput,
  UpdateSpaceInput,
} from '../types/firestore';

/**
 * Create a new space
 * - Creates space document in Firestore
 * - Creates corresponding Drive folder (optional if Drive API not loaded)
 * - Adds creator as owner member
 */
export async function createSpace(
  input: CreateSpaceInput,
  driveFolderId?: string
): Promise<Space> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to create a space');
  }

  // Create space document (only include defined fields - Firestore doesn't allow undefined)
  const spaceData: any = {
    name: input.name,
    description: input.description || '',
    type: input.type || 'general',
    color: input.color || '#6366f1',
    ownerId: user.uid,
    ownerEmail: user.email!,
    driveFolderId: driveFolderId || null, // Can be null if Drive API not loaded
    driveShared: false, // Will be true when shared with members
    googleCalendarSyncEnabled: input.googleCalendarSyncEnabled || false,
    settings: {
      defaultView: 'calendar' as const,
      allowMemberInvites: input.settings?.allowMemberInvites ?? true,
      visibility: input.settings?.visibility || 'private',
    },
    stats: {
      memberCount: 1, // Owner is first member
      noteCount: 0,
      eventCount: 0,
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  // Only add optional fields if they have values (Firestore doesn't allow undefined)
  if (input.icon) {
    spaceData.icon = input.icon;
  }
  if (input.customLabel) {
    spaceData.customLabel = input.customLabel;
  }
  if (input.features) {
    spaceData.features = input.features;
  }

  const docRef = await addDoc(spacesCollection, spaceData as any);

  // Add owner as first member
  await addSpaceMember({
    spaceId: docRef.id,
    userId: user.uid,
    email: user.email!,
    displayName: user.displayName || undefined,
    photoURL: user.photoURL || undefined,
    role: 'owner',
  });

  // Return created space with ID
  return {
    ...spaceData,
    id: docRef.id,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  } as Space;
}

/**
 * Get a space by ID
 * Only members can read space details
 */
export async function getSpace(spaceId: string): Promise<Space | null> {
  const docRef = doc(spacesCollection, spaceId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  } as Space;
}

/**
 * Get all spaces where user is a member
 * Returns spaces ordered by most recently updated
 *
 * This implementation queries through space_members first (which is allowed by security rules),
 * then fetches each space individually (which is allowed because we're members).
 */
export async function getUserSpaces(): Promise<Space[]> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  // First, get all space memberships for this user
  // Security rules allow users to query their own memberships
  const membershipsQuery = query(
    membersCollection,
    where('userId', '==', user.uid),
    where('status', '==', 'active')
  );

  const membershipsSnap = await getDocs(membershipsQuery);

  if (membershipsSnap.empty) {
    return [];
  }

  // Extract space IDs from memberships
  const spaceIds = membershipsSnap.docs.map((doc) => doc.data().spaceId);

  // Fetch each space individually by ID
  // This respects security rules because we're members of these spaces
  const spacePromises = spaceIds.map(async (spaceId) => {
    try {
      const spaceDoc = await getDoc(doc(spacesCollection, spaceId));
      if (spaceDoc.exists()) {
        const spaceData = spaceDoc.data();
        // Filter out soft-deleted spaces
        if (spaceData.deletedAt) {
          return null;
        }
        return {
          ...spaceData,
          id: spaceDoc.id,
        } as Space;
      }
      return null;
    } catch (error) {
      console.error(`Failed to fetch space ${spaceId}:`, error);
      return null;
    }
  });

  const spaces = await Promise.all(spacePromises);

  // Filter out null values and sort by updatedAt
  const validSpaces = spaces.filter((space): space is Space => space !== null);

  // Sort by updatedAt descending (most recent first)
  validSpaces.sort((a, b) => {
    const aTime = a.updatedAt instanceof Timestamp ? a.updatedAt.toMillis() : 0;
    const bTime = b.updatedAt instanceof Timestamp ? b.updatedAt.toMillis() : 0;
    return bTime - aTime;
  });

  return validSpaces;
}

/**
 * Get spaces owned by current user
 */
export async function getOwnedSpaces(): Promise<Space[]> {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated');
  }

  const q = query(
    spacesCollection,
    where('ownerId', '==', user.uid),
    orderBy('updatedAt', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((docSnap) => ({
    // @ts-ignore
id: docSnap.id,
    ...docSnap.data(),
  })) as Space[];
}

/**
 * Update space settings
 * Only owner can update
 */
export async function updateSpace(
  spaceId: string,
  input: UpdateSpaceInput
): Promise<void> {
  const docRef = doc(spacesCollection, spaceId);

  const updateData: any = {
    ...input,
    updatedAt: serverTimestamp(),
  };

  // If settings are being updated, merge with existing
  if (input.settings) {
    // Get current space to merge settings
    const currentSpace = await getSpace(spaceId);
    if (currentSpace) {
      updateData.settings = {
        ...currentSpace.settings,
        ...input.settings,
      };
    }
  }

  await updateDoc(docRef, updateData);
}

/**
 * Delete a space (soft delete)
 * Only owner can delete
 */
export async function deleteSpace(spaceId: string): Promise<void> {
  const docRef = doc(spacesCollection, spaceId);

  await updateDoc(docRef, {
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Permanently delete a space
 * Use with caution - this removes the space from Firestore
 * Drive folder must be deleted separately
 */
export async function permanentlyDeleteSpace(spaceId: string): Promise<void> {
  const docRef = doc(spacesCollection, spaceId);
  await deleteDoc(docRef);
}

/**
 * Update space statistics
 * Called when members/notes/events change
 */
export async function updateSpaceStats(
  spaceId: string,
  stats: Partial<Space['stats']>
): Promise<void> {
  const docRef = doc(spacesCollection, spaceId);
  const currentSpace = await getSpace(spaceId);

  if (!currentSpace) {
    throw new Error('Space not found');
  }

  await updateDoc(docRef, {
    stats: {
      ...currentSpace.stats,
      ...stats,
    },
    updatedAt: serverTimestamp(),
  });
}

/**
 * Add a member to a space
 * Internal helper function used by createSpace and invitation system
 */
async function addSpaceMember(memberData: {
  spaceId: string;
  userId: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  invitedBy?: string;
}): Promise<void> {
  // Create member document with composite ID: userId_spaceId
  const memberId = `${memberData.userId}_${memberData.spaceId}`;
  const memberDocRef = doc(membersCollection, memberId);

  const member: any = {
    spaceId: memberData.spaceId,
    userId: memberData.userId,
    email: memberData.email,
    role: memberData.role,
    permissions: getDefaultPermissions(memberData.role),
    status: 'active',
    joinedAt: serverTimestamp() as Timestamp,
  };

  // Only add optional fields if they are defined (Firestore doesn't allow undefined)
  if (memberData.displayName !== undefined) {
    member.displayName = memberData.displayName;
  }
  if (memberData.photoURL !== undefined) {
    member.photoURL = memberData.photoURL;
  }
  if (memberData.invitedBy !== undefined) {
    member.invitedBy = memberData.invitedBy;
  }

  await setDoc(memberDocRef, member);
}

/**
 * Get default permissions based on role
 */
function getDefaultPermissions(role: 'owner' | 'admin' | 'member' | 'viewer') {
  switch (role) {
    case 'owner':
      return {
        canCreateNotes: true,
        canEditNotes: true,
        canDeleteNotes: true,
        canCreateEvents: true,
        canEditEvents: true,
        canDeleteEvents: true,
        canInviteMembers: true,
        canManageMembers: true,
        canChangeSettings: true,
      };
    case 'admin':
      return {
        canCreateNotes: true,
        canEditNotes: true,
        canDeleteNotes: true,
        canCreateEvents: true,
        canEditEvents: true,
        canDeleteEvents: true,
        canInviteMembers: true,
        canManageMembers: true,
        canChangeSettings: false, // Only owner can change settings
      };
    case 'member':
      return {
        canCreateNotes: true,
        canEditNotes: true,
        canDeleteNotes: false, // Can only delete own notes
        canCreateEvents: true,
        canEditEvents: true,
        canDeleteEvents: false, // Can only delete own events
        canInviteMembers: false,
        canManageMembers: false,
        canChangeSettings: false,
      };
    case 'viewer':
      return {
        canCreateNotes: false,
        canEditNotes: false,
        canDeleteNotes: false,
        canCreateEvents: false,
        canEditEvents: false,
        canDeleteEvents: false,
        canInviteMembers: false,
        canManageMembers: false,
        canChangeSettings: false,
      };
  }
}
